import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import type { UserFiltersDTO } from '@libs/dtos/user/user.dto';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { RoleService } from '@services/user/role.service';
import { UserService } from '@services/user/user.service';
import { TableBase } from '@shared/bases/table.base';
import type { InlineFilterField } from '@shared/components/inline-filters/inline-filters.component';
import { TableComponent } from '@shared/components/table/table.component';
import { TableAction, TableColumn, TagData } from '@shared/components/table/table.model';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { activeLocale, formatDate } from 'app/core/utils/locale';
import { UserCrudComponent } from './user-crud/user-crud.component';
import { UserDetailComponent } from './user-detail/user-detail.component';
import type { UserListItem, UserRow } from './user-row.model';

@Component({
	selector: 'app-users',
	imports: [SharedModule, TableComponent, UserCrudComponent, UserDetailComponent],
	templateUrl: './users.component.html',
	styleUrl: './users.component.scss',
})
export class UsersComponent extends TableBase implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	@ViewChild('userCrud') userCrud!: UserCrudComponent;
	@ViewChild('userDetail') userDetail!: UserDetailComponent;

	data: UserRow[] = [];

	headers: TableColumn<UserRow>[] = [];

	// ====== Pagination Configuration ======
	override mainField = 'fullName';
	override sortField = 'name';
	override sortOrder = 1;
	override limit = 10;
	loadError = false;

	// ====== Filter Configuration (initialized in ngOnInit with translations)
	filtersLoading = true;
	private statusFilter: InlineFilterField[] = [];

	constructor(
		private userService: UserService,
		private messageAlertService: MessageAlertService,
		private cdr: ChangeDetectorRef,
		override viewportService: ViewportService,
		private confirmDialogService: ConfirmDialogService,
		private translocoService: TranslocoService,
		private roleService: RoleService,
	) {
		super(viewportService);
	}

	override loadData(): void {
		this.loadUsers();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;

		// Initialize headers and filters with translations - wait for translations to load
		this.translocoService
			.selectTranslate('users.table.headers.name')
			.pipe(takeUntilDestroyed(this.destroyRef)) // it never completes: bound to the page
			.subscribe(() => {
				this.headers = [
					{
						field: 'fullName', // Display field (computed from name + lastName)
						header: this.translocoService.translate('users.table.headers.name'),
						type: 'name' as const,
						showAvatar: true,
						imageField: 'profilePhotoUrl',
						extraField: 'lastName2', // Show second last name below
						sortField: 'name', // Backend field to use for sorting
					},
					{ field: 'email', header: this.translocoService.translate('users.table.headers.email') },
					{
						field: 'roleTag',
						header: this.translocoService.translate('users.table.headers.role'),
						type: 'tag' as const,
						sortable: false,
					},
				];

				this.statusFilter = [
					{
						key: 'banned',
						label: this.translocoService.translate('users.filter.status'),
						type: 'select' as const,
						options: [
							{ label: this.translocoService.translate('users.crud.fields.active'), value: false },
							{ label: this.translocoService.translate('users.crud.fields.banned'), value: true },
						],
						placeholder: this.translocoService.translate('users.filter.all'),
					},
				];

				this.cdr.detectChanges();
				this.loadRoleFilter();
			});

		this.loadUsers();
	}

	/** Role label translated; unknown roles keep their raw name */
	private roleLabel(name?: string): string {
		if (!name) return this.translocoService.translate('users.table.noRole');
		const key = `users.roles.${name}`;
		const label = this.translocoService.translate(key);
		return label === key ? name : label;
	}

	private roleTag(name?: string): TagData {
		const colors: Record<string, TagData['color']> = { admin: 'blue', client: 'green' };
		return { label: this.roleLabel(name), color: (name && colors[name]) || 'gray' };
	}

	/**
	 * Filters appear all at once when the roles arrive (skeleton pills meanwhile), not one then the other.
	 * Re-run on language change only to relabel: the role list itself is fetched once (RoleService cache).
	 */
	private loadRoleFilter(): void {
		this.roleService.getRoleOptions().subscribe({
			next: (roles) => {
				this.filtersLoading = false;
				this.filterFields = [
					{
						key: 'roleId',
						label: this.translocoService.translate('users.roleFilter.label'),
						type: 'select' as const,
						options: roles.map((r) => ({ label: this.roleLabel(r.name), value: String(r.id) })),
						placeholder: this.translocoService.translate('users.roleFilter.all'),
					},
					...this.statusFilter,
				];
				this.cdr.detectChanges();
			},
			error: () => {
				this.filtersLoading = false;
				this.filterFields = [...this.statusFilter];
				// The table stays usable (status filter only); the user is told why the role filter is missing
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('users.crud.messages.rolesLoadError'),
				);
				this.cdr.detectChanges();
			},
		});
	}

	/**
	 * Formats a user for display
	 */
	private formatUser(user: UserListItem): UserRow {
		return {
			...user,
			createdAt: formatDate(user.createdAt, activeLocale(this.translocoService)),
			roleName: this.roleLabel(user.role?.name),
			roleTag: this.roleTag(user.role?.name),
			bannedText: user.banned
				? this.translocoService.translate('users.crud.fields.banned')
				: this.translocoService.translate('users.crud.fields.active'),
			fullName: `${user.name || ''} ${user.lastName || ''}`.trim(),
		};
	}

	/**
	 * Loads users from the backend with pagination and sorting
	 */
	loadUsers(): void {
		this.isLoading = true;
		this.loadError = false;
		this.cdr.markForCheck();

		const params: Partial<UserFiltersDTO> = {
			page: this.page,
			limit: this.limit,
			sort_by: this.sortField,
			sort_order: this.sortOrder === 1 ? 'asc' : 'desc',
		};

		// Add filters if selected
		if (this.currentFilters.banned !== undefined) {
			params.banned = this.currentFilters.banned;
		}
		if (this.currentFilters.roleId) {
			params.roleId = this.currentFilters.roleId;
		}
		if (this.currentFilters.search) {
			params.search = this.currentFilters.search;
		}

		this.userService.getAllUsers(params).subscribe({
			next: (response) => {
				if (response?.data) {
					const formattedData = response.data.map((user) => this.formatUser(user));

					this.data = formattedData;
					this.totalRecords = response.total || formattedData.length;
				}
				this.isLoading = false;
				this.cdr.detectChanges();
			},
			error: () => {
				this.data = [];
				this.totalRecords = 0;
				this.isLoading = false;
				this.loadError = true;
				this.cdr.detectChanges();
			},
		});
	}

	/**
	 * Blocks (bans) a user: it cannot log in until it is activated again. Nothing is deleted
	 * (permanent deletion is a separate action, offered once the user is blocked)
	 * @param user User to block
	 */
	onBlockUser(user: UserRow): void {
		if (!user?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('users.crud.messages.blockErrorNoId'),
			);
			return;
		}

		const fullName = user.fullName || `${user.name || ''} ${user.lastName || ''}`.trim() || user.email;

		this.confirmDialogService.confirmCustom({
			header: this.translocoService.translate('users.crud.messages.blockHeader'),
			message: this.translocoService.translate('users.crud.messages.confirmBlock', { name: fullName }),
			acceptLabel: this.translocoService.translate('users.table.actions.block'),
			acceptButtonStyleClass: 'p-button-danger',
			accept: () => {
				this.userService.banUser(user.id).subscribe({
					next: () => {
						this.messageAlertService.showMessage(
							'success',
							this.translocoService.translate('common.labels.success'),
							this.translocoService.translate('users.crud.messages.blockSuccess'),
						);
						this.applyBanned(user.id, true);
					},
					error: (err) => {
						this.messageAlertService.showMessage(
							'error',
							this.translocoService.translate('common.labels.error'),
							translateApiError(err, this.translocoService, 'users.crud.messages.blockError'),
						);
					},
				});
			},
		});
	}

	/**
	 * Returns available actions for a user
	 * @param user The user to get actions for
	 */
	getMenuActions(user: UserRow): TableAction[] {
		const actions: TableAction[] = [
			{
				id: 1,
				label: this.translocoService.translate('users.table.actions.view'),
				icon: 'Eye',
				action: 'view',
				command: () => this.onViewUser(user),
			},
			{
				id: 2,
				label: this.translocoService.translate('users.table.actions.edit'),
				icon: 'Edit',
				action: 'update',
				command: () => this.onUpdateUser(user),
			},
		];

		// Add actions based on banned status
		if (user.banned) {
			// If banned, show "Activate" and "Delete Permanently"
			actions.push({
				id: 3,
				label: this.translocoService.translate('users.detail.actions.activate'),
				icon: 'Check',
				action: 'unban',
				command: () => this.onUnbanUser(user),
			});
			actions.push({
				id: 4,
				label: this.translocoService.translate('users.detail.actions.deletePermanently'),
				icon: 'Trash',
				action: 'deletePermanently',
				command: () => this.onDeleteUserPermanently(user),
				class: 'red',
				separator: true,
			});
		} else {
			// If active, show "Block" (ban: reversible, nothing is deleted)
			actions.push({
				id: 3,
				label: this.translocoService.translate('users.table.actions.block'),
				icon: 'Lock',
				action: 'block',
				command: () => this.onBlockUser(user),
				class: 'red',
				separator: true,
			});
		}

		return actions;
	}

	/**
	 * Handles menu action triggers for individual actions
	 * @param event Action event containing the action type and user data
	 */
	menuActionTriggered(event: { action: string; data: UserRow | UserRow[] }): void {
		// Every user action works on one user (the row's menu or the single selected row)
		if (!event?.action || !event.data || Array.isArray(event.data)) return;

		switch (event.action) {
			case 'view':
				this.onViewUser(event.data);
				break;
			case 'block':
				this.onBlockUser(event.data);
				break;
			case 'update':
				this.onUpdateUser(event.data);
				break;
			case 'unban':
				this.onUnbanUser(event.data);
				break;
			case 'deletePermanently':
				this.onDeleteUserPermanently(event.data);
				break;
			default:
				break;
		}
	}

	/**
	 * Unbans a user
	 * @param user User to unban
	 */
	onUnbanUser(user: UserRow): void {
		if (!user?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('usersMessages.activateError'),
			);
			return;
		}

		const confirmMessage = this.translocoService.translate('users.crud.messages.confirmActivate', {
			name: user.fullName,
		});

		this.confirmDialogService.confirm(confirmMessage, () => {
			this.userService.unbanUser(user.id).subscribe({
				next: () => {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('usersMessages.activateSuccess'),
					);
					this.applyBanned(user.id, false);
				},
				error: (err: unknown) => {
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						translateApiError(err, this.translocoService, 'usersMessages.activateError'),
					);
				},
			});
		});
	}

	/**
	 * Permanently deletes a user (physical deletion)
	 * @param user User to delete permanently
	 */
	onDeleteUserPermanently(user: UserRow): void {
		if (!user?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('usersMessages.deletePermanentlyError'),
			);
			return;
		}

		const confirmMessage = this.translocoService.translate('users.crud.messages.confirmDeletePermanently', {
			name: user.fullName,
		});

		this.confirmDialogService.confirmDelete(confirmMessage, () => {
			this.userService.deleteUser(user.id).subscribe({
				next: () => {
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate('usersMessages.deletePermanentlySuccess'),
					);
					this.loadUsers();
				},
				error: () => {
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						this.translocoService.translate('usersMessages.deletePermanentlyError'),
					);
					this.loadUsers();
				},
			});
		});
	}

	/**
	 * Handles sort events from the table
	 * @param event Sort event containing field and direction
	 */

	/**
	 * Opens the user form in update mode.
	 * @param user User to update.
	 */
	onUpdateUser(user: UserRow): void {
		if (!user?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('users.crud.messages.editErrorNoId'),
			);
			return;
		}

		if (!this.userCrud) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('users.crud.messages.editErrorNoComponent'),
			);
			return;
		}

		this.userCrud.show(user);
	}

	/**
	 * Handles user creation/update event - refreshes data after operation completes
	 */
	onUserUpdated(): void {
		this.loadUsers();
	}

	/**
	 * Block/unblock answers only with a message: the row is updated here. With the status filter on,
	 * the user no longer matches it and leaves the page.
	 */
	private applyBanned(userId: number, banned: boolean): void {
		const filter = this.currentFilters.banned;
		const leavesFilter = filter !== undefined && filter !== null && String(filter) !== String(banned);
		if (leavesFilter) {
			this.data = this.data.filter((u) => u.id !== userId);
			this.totalRecords = Math.max(0, this.totalRecords - 1);
		} else {
			const bannedText = this.translocoService.translate(
				banned ? 'users.crud.fields.banned' : 'users.crud.fields.active',
			);
			this.data = this.data.map((u) => (u.id === userId ? { ...u, banned, bannedText } : u));
		}
		this.cdr.detectChanges();
	}

	/**
	 * Handles row selection event
	 */
	onRowSelected(user: UserRow): void {
		this.onViewUser(user);
	}

	/**
	 * Opens the user detail modal
	 * @param user User to view
	 */
	onViewUser(user: UserRow): void {
		if (!user?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('users.crud.messages.viewErrorNoId'),
			);
			return;
		}

		if (!this.userDetail) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('users.crud.messages.viewErrorNoComponent'),
			);
			return;
		}

		this.userDetail.show(user);
	}

	/**
	 * Handles edit request from user detail
	 * @param user User to edit
	 */
	onEditFromDetail(user: UserRow): void {
		this.onUpdateUser(user);
	}

	/**
	 * Handles block request from user detail
	 * @param user User to block
	 */
	onBlockFromDetail(user: UserRow): void {
		this.onBlockUser(user);
	}

	/**
	 * Handles unban request from user detail
	 * @param user User to unban
	 */
	onUnbanFromDetail(user: UserRow): void {
		this.onUnbanUser(user);
	}

	/**
	 * Handles permanent deletion request from user detail
	 * @param user User to delete permanently
	 */
	onDeletePermanentlyFromDetail(user: UserRow): void {
		this.onDeleteUserPermanently(user);
	}
}
